import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Clock, Mail, Phone, ShieldCheck, Star } from "lucide-react";
import content from "@/data/content.json";
import { AccentH2, Container, Label, Reveal, Section } from "./Primitives";
import { FlowLines } from "./FlowLines";

const { kontakt, meta, ansprechpartner: person } = content;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const field =
  "w-full rounded-[10px] border border-[#d4d8d5] bg-[#f8f9f8] px-4 py-3.5 text-[16px] text-[#1e2532] outline-none placeholder:text-[#9aa1ad] focus:border-[#0f9d6e] focus:bg-white";

const empty = {
  name: "",
  company: "",
  email: "",
  phone: "",
  interests: [],
  message: "",
  consent: false,
  newsletter: false,
};

export const Kontakt = () => {
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleInterest = (value) =>
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(value)
        ? f.interests.filter((x) => x !== value)
        : [...f.interests, value],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.company || !form.email || !form.phone || !form.consent) {
      toast.error("Bitte füllen Sie alle Pflichtfelder aus und bestätigen Sie die Einwilligung.");
      return;
    }
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm(empty);
      if (typeof window.fbq === "function") {
        window.fbq("track", "CompleteRegistration");
      }
      toast.success("Vielen Dank. Wir melden uns innerhalb von 24 Stunden.");
    } catch (err) {
      toast.error("Die Anfrage konnte nicht gesendet werden. Bitte rufen Sie uns an.");
    } finally {
      setSending(false);
    }
  };

  return (
    <Section
      id="kontakt"
      testId="section-kontakt"
      className="has-lines overflow-hidden bg-[#1e2532] text-white on-dark"
    >
      <FlowLines />
      <Container>
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-[0.78fr_1.22fr] lg:gap-14">
          <Reveal>
            <Label>{kontakt.label}</Label>
            <AccentH2 className="mt-6 font-bold">{kontakt.h2}</AccentH2>
            <p className="mt-6 max-w-[420px] text-[16.5px] leading-[1.76] text-white/65">{kontakt.intro}</p>

            <div className="mt-7 flex flex-wrap gap-2.5">
              {kontakt.badges.map((b, i) => (
                <span
                  key={b}
                  className="inline-flex items-center gap-2 rounded-full border border-white/14 bg-white/[0.05] px-3.5 py-2 text-[13.5px] text-white/80"
                >
                  {i === 0 ? (
                    <Star size={13} strokeWidth={0} className="fill-[#FBBC05]" />
                  ) : i === 1 ? (
                    <ShieldCheck size={14} className="text-[#3ecf9b]" />
                  ) : (
                    <Clock size={14} className="text-[#3ecf9b]" />
                  )}
                  {b}
                </span>
              ))}
            </div>

            <div className="mt-8 space-y-3">
              <a href={meta.phoneHref} className="flex items-center gap-3.5 text-[16px] text-white/85">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/14 bg-white/[0.05]">
                  <Phone size={16} className="text-[#3ecf9b]" />
                </span>
                <span className="link-underline">{meta.phone}</span>
              </a>
              <a href={`mailto:${meta.email}`} className="flex items-center gap-3.5 text-[16px] text-white/85">
                <span className="flex h-10 w-10 items-center justify-center rounded-full border border-white/14 bg-white/[0.05]">
                  <Mail size={16} className="text-[#3ecf9b]" />
                </span>
                <span className="link-underline">{meta.email}</span>
              </a>
            </div>

            <div
              data-testid="contact-person-card"
              className="mt-9 rounded-[16px] border border-white/12 bg-white/[0.04] p-6"
            >
              <p className="text-[11.5px] font-bold uppercase tracking-[0.18em] text-[#3ecf9b]">
                Ihr Ansprechpartner
              </p>
              <div className="mt-4 flex items-start gap-4">
                <img
                  src={person.image}
                  alt={person.imageAlt}
                  loading="eager"
                  decoding="async"
                  className="h-16 w-16 shrink-0 rounded-full object-cover object-top"
                />
                <div>
                  <p className="text-[16px] font-semibold text-white">{person.name}</p>
                  <p className="text-[14px] text-white/55">{person.role}</p>
                  <p className="mt-3 text-[14.5px] leading-[1.6] text-white/70">„{person.quote}“</p>
                  <a
                    href={meta.phoneHref}
                    className="mt-3 inline-flex items-center gap-2 text-[14px] font-semibold text-[#3ecf9b]"
                  >
                    <Phone size={13} />
                    <span className="link-underline">{meta.phone}</span>
                  </a>
                </div>
              </div>
            </div>

            <p className="mt-8 border-t border-white/12 pt-6 text-[14.5px] text-white/50">
              {meta.company} · {meta.street} · {meta.zip} {meta.city}
            </p>
          </Reveal>

          <Reveal delay={0.12}>
            {done ? (
              <div
                data-testid="contact-success"
                className="rounded-[20px] border border-[#0f9d6e]/40 bg-white p-10 text-[#1e2532] md:p-14"
              >
                <h3 className="font-semibold text-[#1e2532]">Ihre Anfrage ist eingegangen.</h3>
                <p className="mt-4 text-[16.5px] leading-[1.78] text-[#565f70]">
                  Wir prüfen Ihre Angaben und melden uns innerhalb von 24 Stunden bei Ihnen. Wenn es schneller
                  gehen soll, erreichen Sie uns telefonisch unter {meta.phone}.
                </p>
              </div>
            ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="rounded-[20px] border border-[#dfe2e0] bg-white p-7 text-[#1e2532] md:p-10"
                noValidate
              >
                <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
                  <label className="block">
                    <span className="mb-2 block text-[13.5px] font-semibold text-[#3c4356]">Name *</span>
                    <input
                      data-testid="contact-name"
                      className={field}
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      autoComplete="name"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-[13.5px] font-semibold text-[#3c4356]">Unternehmen *</span>
                    <input
                      data-testid="contact-company"
                      className={field}
                      value={form.company}
                      onChange={(e) => set("company", e.target.value)}
                      autoComplete="organization"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-[13.5px] font-semibold text-[#3c4356]">E-Mail *</span>
                    <input
                      data-testid="contact-email"
                      type="email"
                      className={field}
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      autoComplete="email"
                    />
                  </label>
                  <label className="block">
                    <span className="mb-2 block text-[13.5px] font-semibold text-[#3c4356]">Rufnummer *</span>
                    <input
                      data-testid="contact-phone"
                      className={field}
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      autoComplete="tel"
                      type="tel"
                      required
                    />
                  </label>
                </div>

                <fieldset className="mt-8">
                  <legend className="mb-4 text-[13.5px] font-semibold text-[#3c4356]">
                    Interesse (Mehrfachauswahl möglich)
                  </legend>
                  <div className="flex flex-wrap gap-1.5 sm:gap-2.5">
                    {kontakt.interests.map((int) => {
                      const active = form.interests.includes(int);
                      return (
                        <button
                          type="button"
                          key={int}
                          data-testid={`contact-interest-${int}`}
                          onClick={() => toggleInterest(int)}
                          className={`rounded-full border px-3 py-1.5 text-[12.5px] font-medium sm:px-4 sm:py-2.5 sm:text-[14px] ${
                            active
                              ? "border-[#0f9d6e] bg-[#0f9d6e]/10 text-[#0b7d57]"
                              : "border-[#d4d8d5] bg-[#f8f9f8] text-[#565f70] hover:border-[#9aa1ad]"
                          }`}
                          style={{ transition: "border-color .3s ease, background-color .3s ease, color .3s ease" }}
                          aria-pressed={active}
                        >
                          {int}
                        </button>
                      );
                    })}
                  </div>
                </fieldset>

                <label className="mt-6 block">
                  <span className="mb-2 block text-[13.5px] font-semibold text-[#3c4356]">Nachricht</span>
                  <textarea
                    data-testid="contact-message"
                    rows={5}
                    className={field}
                    value={form.message}
                    onChange={(e) => set("message", e.target.value)}
                  />
                </label>

                <label className="mt-8 flex items-start gap-3 text-[14.5px] leading-[1.6] text-[#565f70]">
                  <input
                    data-testid="contact-consent"
                    type="checkbox"
                    className="mt-1 h-[18px] w-[18px] shrink-0"
                    checked={form.consent}
                    onChange={(e) => set("consent", e.target.checked)}
                  />
                  <span>
                    Ich habe die{" "}
                    <a href="/datenschutzerklaerung/" className="link-underline font-semibold text-[#1e2532]">
                      Datenschutzerklärung
                    </a>{" "}
                    gelesen und willige in die Verarbeitung meiner Angaben zur Bearbeitung meiner Anfrage ein. *
                  </span>
                </label>

                <label className="mt-4 flex items-start gap-3 text-[14.5px] leading-[1.6] text-[#565f70]">
                  <input
                    data-testid="contact-newsletter"
                    type="checkbox"
                    className="mt-1 h-[18px] w-[18px] shrink-0"
                    checked={form.newsletter}
                    onChange={(e) => set("newsletter", e.target.checked)}
                  />
                  <span>
                    Ich willige ein, fortlaufend Neuigkeiten und Informationen von energyleads per E-Mail zu
                    erhalten. Die Einwilligung kann ich jederzeit widerrufen. (optional)
                  </span>
                </label>

                <button
                  type="submit"
                  data-testid="contact-submit"
                  disabled={sending}
                  className="btn-solid mt-9 w-full justify-center inline-flex items-center px-8 py-[17px] text-[14px] font-bold uppercase tracking-[0.06em] disabled:opacity-60"
                >
                  <span>{sending ? "Wird gesendet …" : kontakt.submit}</span>
                </button>
                <p className="mt-4 text-center text-[13px] text-[#8a90a0]">
                  Antwort innerhalb von 24 Stunden · Kostenlos und unverbindlich
                </p>
              </form>
            )}
          </Reveal>
        </div>
      </Container>
    </Section>
  );
};
